import YoutubeIframe from "components/youtube_iframe";
import FloatingMenu from "components/floating_menu";
import Link from "next/link";

import { humanizedTime } from "utils/date";
import * as lessonsWatchedApi from "api/lessons_watched";
import clsx from "clsx";
import {
  FiPlay,
  FiMoreHorizontal,
  FiEye,
  FiEyeOff,
  FiX,
  FiLoader,
  FiEdit2
} from "react-icons/fi";
import { track, increment } from "utils/tracker";
import { questionnairePath } from "utils/questionnaire";

import { useState, useRef } from "react";
import { useNotifications } from "hooks/notifications";

export default function Lesson(props) {
  const {
    id,
    title,
    topics,
    channelName,
    channelUrl,
    duration,
    videoUrl,
    thumbnailUrl,
    watchedAt
  } = props;
  const { user } = props;
  const { loading } = props;

  const subjectInfo = topics[0];
  const subject = subjectInfo.name;
  const topic = subjectInfo.children[0]?.name;
  const embedId = /v=(.*)/.exec(videoUrl)[1];

  const [watching, setWatching] = useState(false);
  const watchingRef = useRef(null);
  watchingRef.current = watching;
  const [showMenu, setShowMenu] = useState(false);
  const { setNotification } = useNotifications();
  const [updateingWatcheAt, setUpdatingWatchedAt] = useState(false);

  function handleWatch() {
    if (!user) return notifyAnonymous();

    setWatching(true);
    setTimeout(() => {
      if (!watchingRef.current) return;
      updateWatched();
    }, 10000);
  }

  function handleWatchedUpdate() {
    trackWatchedLesson();
    if (!user) return notifyAnonymous();

    setUpdatingWatchedAt(true);
    const promise = watchedAt
      ? lessonsWatchedApi.destroy(id)
      : lessonsWatchedApi.create(id);
    promise.then(handleWatchedUpdateResponse);
  }

  function updateWatched() {
    trackWatchedLesson();
    lessonsWatchedApi.create(id).then(handleWatchedUpdateResponse);
  }

  function handleWatchedUpdateResponse(response) {
    const newWatchedAt = watchedAt ? null : response.body.watchedAt;
    setTimeout(() => {
      setShowMenu(false);
      props.onChange({ watchedAt: newWatchedAt });
      setUpdatingWatchedAt(false);
    }, 300);
  }

  function handleCloseLesson() {
    if (!watching) return notifyClosedLesson();

    setWatching(false);
    setShowMenu(false);
  }

  function notifyClosedLesson() {
    setNotification({
      title: "A aula já está fechada",
      text: "Você só pode fechar a aula enquanto ela estiver sendo reproduzida"
    });
  }

  function notifyAnonymous() {
    setNotification({
      title: "Você precisa estar logado",
      text: `<a href="/cadastro?redirect_to=/aulas">Crie uma conta</a> gratuita para assistir as aulas`
    });
  }

  function normalizedLesson() {
    const subject = topics[0];
    const topic = subject && subject.children[0];
    return {
      id: id,
      title: title,
      channelName: channelName,
      subject: subject && subject.name,
      topic: topic && topic.name
    };
  }

  function trackWatchedLesson() {
    track("Watched lesson", normalizedLesson());
    increment("lesson_views");
  }

  function WatchedAtAction() {
    let icon, text;
    if (updateingWatcheAt) {
      icon = <FiLoader className="animate-spin" />;
    } else {
      if (watchedAt) icon = <FiEyeOff size={18} />;
      else icon = <FiEye size={18} />;
    }

    if (watchedAt) text = "Marcar como não assistida";
    else text = "Marcar como assistida";

    return (
      <div className="flex items-center">
        {icon}
        {text}
      </div>
    );
  }

  function Options() {
    return (
      <div className="relative">
        <div
          className="menu-trigger cursor-pointer"
          onClick={() => setShowMenu(true)}
        >
          <FiMoreHorizontal size={20} />
        </div>
        {showMenu && (
          <FloatingMenu
            visible={showMenu}
            onChange={(visible) => setShowMenu(visible)}
          >
            <li
              className={user ? "" : "text-disabled"}
              onClick={handleWatchedUpdate}
            >
              <WatchedAtAction />
            </li>
            <li>
              <Link href={questionnairePath({ topics })}>
                <a className="flex items-center no-underline">
                  <FiEdit2 size={18} />
                  Resolver questões
                </a>
              </Link>
            </li>
            <li
              onClick={handleCloseLesson}
              className={watching ? "" : "text-disabled"}
            >
              <FiX size={18} />
              Fechar aula
            </li>
          </FloatingMenu>
        )}
      </div>
    );
  }

  const imageOrVideo = watching ? (
    <div className="z-10">
      <YoutubeIframe
        embedId={`${embedId}?autoplay=1`}
        className="h-48 xl:h-96"
      />
    </div>
  ) : (
    <div
      className="image-wrapper hover:bg text-[0px] relative flex-shrink-0 h-48 cursor-pointer xl:w-64 xl:h-36"
      onClick={handleWatch}
    >
      {thumbnailUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={thumbnailUrl}
          alt="Miniatura da aula"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="play-wrapper">
        <div className="background absolute left-0 top-0 w-full h-full bg-black opacity-5"></div>
        <div className="icon-wrapper absolute-center text-white opacity-60">
          <FiPlay size={42} fill="white" className="drop-shadow-md filter" />
        </div>
      </div>

      <style jsx>
        {`
          .image-wrapper:hover .icon-wrapper {
            opacity: 1;
          }

          .play-wrapper:hover .background {
            opacity: 0.2;
          }
        `}
      </style>
    </div>
  );

  const border = clsx(
    "border-t-[3px] border-b-1 xl:border-r-[3px] ",
    "rounded-b-xl xl:rounded-l-xl",
    {
      "xl:rounded-br-none border border-gray-100 xl:border-t xl:border-t-gray-100":
        !watching
    },
    {
      "xl:rounded-b-xl xl:rounded-tl-none xl:border-t-[3px] xl:border-r-0":
        watching
    },
    { "xl:border-r-success border-t-success": watchedAt },
    { "xl:border-r-primary-300 border-t-primary-300": !watchedAt },
    { "xl:border-t-success": watching && watchedAt },
    { "xl:border-t-primary-300": watching && !watchedAt }
  );

  return (
    <div
      className={clsx(
        "lesson",
        { "xl:flex-col-reverse": watching, loading },
        border,
        "flex flex-col-reverse w-full xl:flex-row",
        "fade-loading last:mb-0 mb-4 shadow overflow-hidden"
      )}
    >
      {imageOrVideo}
      <div className="content flex flex-grow">
        <div className="metadata flex flex-col flex-grow justify-between px-4 py-3">
          <div>
            <div className="flex items-start">
              <span className="block flex-grow mb-2 mr-2 text-lg font-semibold">
                {title}
              </span>
              <Options />
            </div>
            <div className="topics flex flex-wrap items-center">
              <span className="mr-2 text-emphasis-medium">{subject}</span>
              {topic && (
                <div className="flex items-center">
                  <div className="circle h-[3px] w-[3px] inline-block mr-2 bg-emphasis-low rounded-full"></div>
                  <span className="text-emphasis-medium">{topic}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-end justify-between">
            <a
              href={channelUrl}
              target="_blank"
              className="mt-4 text-emphasis-medium"
              rel="noreferrer"
            >
              {channelName}
            </a>
            <span className="duration ml-2">{humanizedTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
